import { ApiClient, ApiClientConfig } from "../api-client";
import { ApiClientError, handleApiError, handleApiErrorWithSchema } from "../errors";
import schemaValidators from "./schema-validators";

export default class MediaClient extends ApiClient<typeof schemaValidators> {
	constructor(config: ApiClientConfig) {
		super(config, schemaValidators);
	}

	getNextRecordingNumbers = async ({ sessionId }: { sessionId: string }) => {
		const searchParams = new URLSearchParams([
			["number", "webcam"],
			["number", "screencapture"],
			["next", "true"],
		]);

		const data = await handleApiErrorWithSchema(
			super.fetch(`${this.config.verityOrigin}/api/verity/quiz/recording/${sessionId}?${searchParams.toString()}`),
			schemaValidators.getNextRecordingNumbers,
		);

		return {
			internal: data.screencapture,
			external: data.webcam,
		};
	};

	uploadRecording = async ({
		sessionId,
		recordingDetails,
	}: {
		sessionId: string;
		recordingDetails: {
			recordingType: "display" | "webcam";
			videoNumber: number;
			videoObjectUrl: string;
			videoMimeType: string;
			blobNumber: number;
		};
	}) => {
		const videoBlob = new Blob([await fetch(recordingDetails.videoObjectUrl).then((r) => r.arrayBuffer())], {
			type: recordingDetails.videoMimeType,
		});

		const searchParams = new URLSearchParams({
			segment: `${recordingDetails.blobNumber}`,
			url: "upload",
		});
		const response = await handleApiError(
			super.fetch(
				`${this.config.verityOrigin}/api/verity/quiz/recording/${sessionId}/${
					recordingDetails.recordingType === "display" ? "screencapture" : recordingDetails.recordingType
				}/${recordingDetails.videoNumber}?${searchParams.toString()}`,
			),
		);

		/**
		 * If proctoring service returns 409, assume failure and do not retry
		 */
		if (response.status === 409) {
			throw new Error("409");
		} else if (!response.ok) {
			throw new ApiClientError(new Error(`Unable to get upload URL: ${response.status} - ${response.statusText}`));
		}

		const data = (await response.json()) as unknown;
		if (!schemaValidators.uploadRecording.validate(data)) {
			throw new ApiClientError(new Error(`Invalid upload URL: ${JSON.stringify(data)}`));
		}

		const s3Response = await fetch(data.url, {
			method: "PUT",
			body: videoBlob,
		});
		if (!s3Response.ok) {
			throw new ApiClientError(new Error(`Upload to S3 failed: ${s3Response.status} - ${s3Response.statusText}`));
		}
	};

	uploadPhoto = async ({
		sessionId,
		type,
		imageObjectUrl,
		mimeType,
	}: {
		sessionId: string;
		type: "person" | "id";
		imageObjectUrl: string;
		mimeType: string;
	}) => {
		const imageBlob = new Blob([await fetch(imageObjectUrl).then((r) => r.arrayBuffer())], { type: mimeType });
		await handleApiError(
			super.fetch(`${this.config.verityOrigin}/api/verity/photo/${sessionId}-${type}.png`, {
				method: "PUT",
				body: imageBlob,
			}),
		);
	};
}
